/** Pure practice-player logic (unit tested): speed steps, A–B loop, resume position, time format. */

export const RATE_MIN = 0.5;
export const RATE_MAX = 1.25;
export const RATE_STEP = 0.05;
export const SKIP_SECONDS = 5;
/** Shortest A–B loop (seconds). */
export const MIN_LOOP = 0.5;

/** Snap to the 0.05 grid and clamp to 0.5–1.25. */
export function clampRate(r: number): number {
  if (!Number.isFinite(r)) return 1;
  const snapped = Math.round(r / RATE_STEP) * RATE_STEP;
  return Number(Math.min(RATE_MAX, Math.max(RATE_MIN, snapped)).toFixed(2));
}

export function stepRate(r: number, dir: 1 | -1): number {
  return clampRate(clampRate(r) + dir * RATE_STEP);
}

/** 75 → "1:15", 3725 → "1:02:05". */
export function formatTime(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) sec = 0;
  const s = Math.floor(sec);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

export interface AbLoop {
  a: number | null;
  b: number | null;
}

export const NO_LOOP: AbLoop = { a: null, b: null };

export function isLoopActive(l: AbLoop): l is { a: number; b: number } {
  return l.a !== null && l.b !== null && l.b - l.a >= MIN_LOOP;
}

/**
 * Set A or B at time t. Setting A after B (or B before A) swaps them; B too close to A is pushed
 * MIN_LOOP after A (clamped to the duration when known).
 */
export function setLoopPoint(loop: AbLoop, which: 'a' | 'b', t: number, duration: number): AbLoop {
  const max = Number.isFinite(duration) && duration > 0 ? duration : Infinity;
  const time = Math.max(0, Math.min(max, t));
  let a = which === 'a' ? time : loop.a;
  let b = which === 'b' ? time : loop.b;
  if (a !== null && b !== null) {
    if (b < a) [a, b] = [b, a];
    if (b - a < MIN_LOOP) b = Math.min(max, a + MIN_LOOP);
    if (b - a < MIN_LOOP) a = Math.max(0, b - MIN_LOOP);
  }
  return { a, b };
}

/** Where to seek when playback reached the end of an active loop (null = keep playing). */
export function loopSeekTarget(t: number, loop: AbLoop): number | null {
  if (!isLoopActive(loop)) return null;
  return t >= loop.b ? loop.a : null;
}

/** Remembered position worth resuming: not the first 3 s and not the last 3 s. */
export function resumePosition(saved: number | null | undefined, duration: number | null | undefined): number {
  if (!saved || !Number.isFinite(saved) || saved < 3) return 0;
  if (duration && Number.isFinite(duration) && duration > 0 && saved > duration - 3) return 0;
  return saved;
}
